'use client';

import React from 'react';
import { GroupByOption, WriteOffStatus, WriteOffType } from '@repo/types';
import { ActionsMenu, ActionsMenuDivider, ActionsMenuItem, Button, Checkbox, Icon, Tooltip } from '@repo/ui';
import { dateUtil, numberUtil, useDarkMode } from '@xtnd/web/shared';
import { type DataTableColumn } from '@xtnd/web/shared/components/DataTable';

import {
  CELL_STYLE,
  DAYS_PAST_DUE,
  WRITEOFF_CATEGORY_LABEL,
  WRITEOFF_STATUS_OPTIONS,
} from '../../constants';
import {
  ActionColumnConfig,
  WriteOff,
  WriteOffItem,
  FlatWriteOffItem,
  WriteOffColumnsConfig,
  WriteOffTabType,
  ActivityColumnConfig,
  WriteOffGroupByClient,
} from '../../types/writeOffs';
import { ArWriteOffCategory, ArWriteOffType } from '../common';

// ============================================================================
// Selection Column
// ============================================================================

/** Returns a unique key for a row based on the current groupBy mode */
export const getWriteOffRowKey = (row: any, groupBy: GroupByOption): string => {
  if (groupBy === GroupByOption.CLIENT) return row.clientId ?? '';
  return row.writeOffId ?? row.invoiceId ?? '';
};

/** Creates a checkbox selection column for multi-select */
export const createSelectColumn = (
  selectedKeys: string[],
  onSelectRow: (e: React.ChangeEvent<HTMLInputElement>, row: any) => void,
  onSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void,
  totalRows: number,
): DataTableColumn => ({
  key: 'select',
  header: (
    <input
      type="checkbox"
      className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
      onChange={onSelectAll}
      checked={totalRows > 0 && selectedKeys.length === totalRows}
    />
  ),
  align: 'left',
  defaultWidth: 40,
  renderCell: (row: any) => (
    <input
      type="checkbox"
      className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
      checked={selectedKeys.includes(row.writeOffId ?? row.clientId ?? row.invoiceId ?? '')}
      onChange={e => onSelectRow(e, row)}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    />
  ),
});

// ============================================================================
// Base Column Factory Functions
// ============================================================================

type ColumnAlignment = 'left' | 'right' | 'center';

interface TextColumnConfig {
  key: string;
  header: string;
  width?: number;
  align?: ColumnAlignment;
  isSortable?: boolean;
  isResizable?: boolean;
  showTooltip?: boolean;
  getValue?: (row: WriteOff) => string | undefined;
}

// Single-line clamp (one line + ellipsis). Inline-styled rather than the Tailwind
// `truncate` utility on purpose — see TruncatedTextCell.
const LINE_CLAMP_1: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '1',
  overflow: 'hidden',
};

/**
 * Renders a single-line, truncating text cell.
 *
 * Uses `-webkit-line-clamp: 1` (one line + ellipsis) instead of Tailwind's
 * `truncate` (`white-space: nowrap`). In this auto-layout / `min-w-full` table,
 * a nowrap cell reports its min-content as the FULL text width — which clips the
 * text early even when the column has room AND floors the column so the resize
 * handle cannot be dragged narrower than the text. line-clamp keeps
 * `white-space: normal`, so the column's min-content is only its longest word:
 * the cell grows to fill available space yet the column can also be resized down
 * freely. The tooltip is always available on hover so a clipped value stays
 * reachable (matching the Comment column's existing behaviour).
 */
const TruncatedTextCell: React.FC<{
  value: string;
  enableTooltip?: boolean;
}> = ({ value, enableTooltip }) => {
  const text = (
    <span className={CELL_STYLE} style={LINE_CLAMP_1}>
      {value}
    </span>
  );

  if (!enableTooltip) return text;

  return (
    <Tooltip
      contentClassName="h-full min-w-[16rem] text-justify"
      content={value}
      position="bottom"
    >
      {text}
    </Tooltip>
  );
};

/**
 * Creates a text column with optional tooltip
 */
const createTextColumn = ({
  key,
  header,
  width = 150,
  align = 'left',
  isSortable,
  isResizable = true,
  showTooltip,
  getValue,
}: TextColumnConfig): DataTableColumn => ({
  key,
  header,
  align,
  isSortable,
  isResizable,
  defaultWidth: width,
  renderCell: row => {
    const value = getValue ? getValue(row) : row[key];
    const displayValue = value || '-';

    return <TruncatedTextCell value={displayValue} enableTooltip={showTooltip} />;
  },
});

interface CurrencyColumnConfig {
  key: string;
  header: string;
  width?: number;
  isSortable?: boolean;
  isResizable?: boolean;
  getValue?: (row: any) => number;
}

/**
 * Creates a currency column
 */
const createCurrencyColumn = ({
  key,
  header,
  width = 150,
  isSortable = true,
  isResizable = true,
  getValue,
}: CurrencyColumnConfig): DataTableColumn => ({
  key,
  header,
  align: 'right',
  isSortable,
  isResizable,
  defaultWidth: width,
  renderCell: row => {
    const value = getValue ? getValue(row) : (row[key] ?? 0);
    return <span className={CELL_STYLE}>{numberUtil.formatCurrency(value)}</span>;
  },
});

interface DateColumnConfig {
  key: string;
  header: string;
  width?: number;
  align?: ColumnAlignment;
  isSortable?: boolean;
  isResizable?: boolean;
  getValue?: (row: WriteOff) => string | Date | null;
  format?: (typeof dateUtil.DateFormat)[keyof typeof dateUtil.DateFormat];
}

/**
 * Creates a date column
 */
const createDateColumn = ({
  key,
  header,
  width = 150,
  align = 'left',
  isSortable,
  isResizable = true,
  getValue,
  format = dateUtil.DateFormat.US,
}: DateColumnConfig): DataTableColumn => ({
  key,
  header,
  align,
  isSortable,
  isResizable,
  defaultWidth: width,
  renderCell: row => {
    const value = getValue ? getValue(row) : row[key];
    if (!value) {
      return <span className={CELL_STYLE}>-</span>;
    }
    const date = typeof value === 'string' ? new Date(value) : value;
    const formatted = dateUtil.formatDate(date, format);
    return <span className={CELL_STYLE}>{formatted}</span>;
  },
});

interface NumberColumnConfig {
  key: string;
  header: string;
  width?: number;
  align?: ColumnAlignment;
  isSortable?: boolean;
  isResizable?: boolean;
  getValue?: (row: any) => number | null | undefined;
  formatValue?: (value: number) => string;
  getClassName?: (value: number | null | undefined) => string;
}

/**
 * Creates a number column
 */
const createNumberColumn = ({
  key,
  header,
  width = 150,
  align = 'right',
  isSortable = true,
  isResizable = true,
  getValue,
  formatValue,
  getClassName,
}: NumberColumnConfig): DataTableColumn => ({
  key,
  header,
  align,
  isSortable,
  isResizable,
  defaultWidth: width,
  renderCell: row => {
    const value = getValue ? getValue(row) : row[key];

    let displayValue = '-';
    if (value != null) {
      displayValue = formatValue ? formatValue(value) : String(value);
    }

    const className = getClassName ? getClassName(value) : CELL_STYLE;
    return <span className={className}>{displayValue}</span>;
  },
});

// ============================================================================
// Shared Column Definitions
// ============================================================================

/** Invoice column with optional split invoice indicator */
const createInvoiceColumn = (showSplitInvoice = false): DataTableColumn =>
  createTextColumn({
    key: 'invoiceNumber',
    header: 'INV #',
    width: 120,
    align: 'left',
    isResizable: true,
    showTooltip: false,
    getValue: row => {
      const baseValue = row.invoiceNumber || '';
      if (showSplitInvoice && (row as any).splitInvoice?.ids?.length) {
        return `${baseValue} (Split Invoice)`;
      }
      return baseValue;
    },
  });

// Removed unused appealStatusColumn

const categoryColumn: DataTableColumn = {
  key: 'category',
  header: 'Category',
  align: 'left',
  isResizable: true,
  defaultWidth: 150,
  renderCell: (row: WriteOffItem) =>
    row.category ? (
      <ArWriteOffCategory category={row.category} />
    ) : (
      <span className={CELL_STYLE}>-</span>
    ),
};

const typeColumn: DataTableColumn = {
  key: 'type',
  header: 'Type',
  align: 'left',
  isResizable: true,
  defaultWidth: 150,
  renderCell: (row: FlatWriteOffItem | WriteOffItem) =>
    row.type ? <ArWriteOffType type={row.type} /> : <span className={CELL_STYLE}>-</span>,
};

const createCategoryTypeColumn = (): DataTableColumn => ({
  key: 'categoryType',
  header: 'Type & Category',
  align: 'left',
  isResizable: true,
  defaultWidth: 160,
  renderCell: (row: FlatWriteOffItem | WriteOffItem) => (
    <div className="flex flex-col gap-0.5 items-center">
      {row.type ? <ArWriteOffType type={row.type} /> : <span className={CELL_STYLE}>-</span>}
      {row.category && (
        <span className="text-xs text-xtnd-dark dark:text-xtnd-white font-medium">
          {WRITEOFF_CATEGORY_LABEL[row.category] || row.category}
        </span>
      )}
    </div>
  ),
});

/**
 * Creates Activity column with callback to open Activity drawer
 */
export const createActivityColumn = (config?: ActivityColumnConfig): DataTableColumn => ({
  key: 'activity',
  header: 'Activity',
  align: 'center',
  width: 'w-10',
  isResizable: false,
  defaultWidth: 80,
  renderCell: row => (
    <div className="flex items-center justify-center">
      <Icon
        name="FaHistory"
        size="xs"
        className="text-xtnd-blue dark:text-xtnd-white cursor-pointer hover:opacity-80"
        onClick={e => {
          e.stopPropagation();
          config?.onOpenActivity?.(row);
        }}
      />
    </div>
  ),
});

// Removed unused paymentSummaryColumn

// ============================================================================
// Tab-Specific Column Factories
// ============================================================================

/**
 * Creates "By" and "Date" columns for each tab type
 * @param headerPrefix - e.g., "Approved", "Processed", "Rejected"
 */
export const createActionerColumns = (
  headerPrefix: 'Approved' | 'Processed' | 'Rejected'
): DataTableColumn[] => [
  createTextColumn({
    key: `${headerPrefix.toLowerCase()}By`,
    header: `${headerPrefix} By`,
    align: 'left',
    isResizable: true,
    showTooltip: false,
    getValue: (row: WriteOff) => {
      const name = row.writeOff?.updatedByName || row.writeOff?.createdByName;
      return name ? name.charAt(0).toUpperCase() + name.slice(1) : '-';
    },
  }),
  createDateColumn({
    key: `${headerPrefix.toLowerCase()}At`,
    header: `${headerPrefix} Date`,
    align: 'left',
    isResizable: true,
    getValue: row => row.writeOff?.updatedAt || row.writeOff?.createdAt || null,
    format: dateUtil.DateFormat.US,
  }),
];

/**
 * Creates the Actions column with configurable menu items
 * Now always shows (not just for admins) with Activity always visible
 */
export const createActionColumn = (
  config: ActionColumnConfig & { activityConfig?: ActivityColumnConfig }
): DataTableColumn => {
  const {
    isAdmin,
    mutatingRows,
    actionsMenuOpen,
    setActionsMenuOpen,
    setMenuTriggerElement,
    menuTriggerElement,
    actions,
  } = config;

  return {
    key: 'action',
    header: 'Actions',
    align: 'center',
    defaultWidth: 100,
    renderCell: row => {
      const rowId = row.writeOffId ?? row.invoiceId;
      return (
        <div className="relative flex items-center justify-center">
          {mutatingRows.has(rowId) ? (
            <Icon name="HiRefresh" className="h-5 w-5 animate-spin text-xtnd-blue m-auto" />
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={e => {
                  e.stopPropagation();
                  const newState = actionsMenuOpen === rowId ? null : rowId;
                  setActionsMenuOpen(newState);
                  setMenuTriggerElement(newState ? (e.currentTarget as HTMLElement) : null);
                }}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="More actions"
                disabled={mutatingRows.has(rowId)}
              >
                <Icon name="HiDotsVertical" className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </Button>

              <ActionsMenu
                isOpen={actionsMenuOpen === rowId}
                onClose={() => setActionsMenuOpen(null)}
                triggerElement={menuTriggerElement}
              >
                {/* Admin actions - only show if isAdmin */}
                {isAdmin &&
                  actions
                    .filter(action => !action.shouldShow || action.shouldShow(row))
                    .map(action => (
                      <span key={action.label}>
                        {action.showDividerBefore && <ActionsMenuDivider />}
                        <ActionsMenuItem
                          icon={action.icon}
                          iconColor={action.iconColor}
                          label={action.label}
                          onClick={(e: any) => {
                            e?.stopPropagation?.();
                            action.onClick?.(row);
                            setActionsMenuOpen(null);
                          }}
                          disabled={mutatingRows.has(rowId)}
                        />
                      </span>
                    ))}
              </ActionsMenu>
            </>
          )}
        </div>
      );
    },
  };
};

export const createInvoiceActionColumn = (
  onOpenActivity?: (row: WriteOff) => void
): DataTableColumn => {
  return {
    key: 'activity',
    header: 'Activity',
    align: 'center',
    defaultWidth: 100,
    renderCell: row => (
      <div className="relative flex items-center justify-center">
        <Button
          variant="ghost"
          onClick={event => {
            event.stopPropagation();
            onOpenActivity?.(row);
          }}
          className="focus:outline-none focus:ring-0"
        >
          <Icon name="HiClock" size="md" className="text-xtnd-blue dark:text-xtnd-white" />
        </Button>
      </div>
    ),
  };
};

const arrowColumn: DataTableColumn = {
  key: '',
  header: '',
  align: 'center',
  width: 'w-10',
  renderCell: () => (
    <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
      <button>
        <Icon name="FaAngleRight" size="sm" className="opacity-100" />
      </button>
    </div>
  ),
};

// ============================================================================
// Group By Client Columns (Shared)
// ============================================================================
const getDaysPastDueColor = (value: number | null | undefined): string => {
  const baseClass = CELL_STYLE;
  return value != null && value > DAYS_PAST_DUE ? `${baseClass} !text-red-500` : baseClass;
};
export const groupByClient = (): DataTableColumn[] => [
  createTextColumn({
    key: 'clientName',
    header: 'Client',
    width: 200,
    showTooltip: true,
  }),
  createNumberColumn({
    key: 'numOfInvoices',
    header: 'Invoices',
    width: 150,
    align: 'right',
    isSortable: false,
    isResizable: true,
  }),
  createCurrencyColumn({
    key: 'total',
    header: 'Total Amount',
    width: 150,
    isSortable: true,
    isResizable: true,
  }),
  {
    key: 'amountPaid',
    header: 'Paid Amount',
    align: 'left',
    isSortable: true,
    isResizable: true,
    defaultWidth: 150,
    renderCell: (row: WriteOff) => (
      <div className="flex flex-col min-w-[8rem]">
        <div className="flex text-xs justify-between">
          <span className="text-gray-600 dark:text-gray-400">Paid</span>
          <span className="text-green-600 font-medium">
            {numberUtil.formatCurrency(row.paid ?? 0)}
          </span>
        </div>

        <div className="flex text-xs justify-between">
          <span className="text-gray-600 dark:text-gray-400">Credits Issued</span>
          <span className="text-gray-900 dark:text-gray-100">
            {numberUtil.formatCurrency(row.creditsIssued ?? 0)}
          </span>
        </div>
      </div>
    ),
  },
  createCurrencyColumn({
    key: 'outstandingBalance',
    header: 'Amount Due',
    width: 150,
  }),
  createNumberColumn({
    key: 'daysPastDue',
    header: 'DPD',
    width: 150,
    align: 'right',
    isSortable: true,
    isResizable: true,
    getValue: (row: WriteOffGroupByClient) => row.maxDaysPastDue,
    getClassName: getDaysPastDueColor,
  }),
  arrowColumn,
];

const AllTabStatusBadge = ({ status }: { status: WriteOffStatus }) => {
  const { isDark } = useDarkMode();
  const mode = isDark ? 'dark' : 'light';

  const option = WRITEOFF_STATUS_OPTIONS[mode]?.[status];
  if (!option) return null;

  let label = option.label;

  // Map labels as requested
  if (status === WriteOffStatus.PENDING) label = 'Proposed';
  if (status === WriteOffStatus.ARCHIVED || status === WriteOffStatus.CLOSED) label = 'Processed';

  return (
    <span
      className={`text-sm inline-flex items-center px-2 py-1 rounded-full border ${option.colorClass} truncate`}
      title={label}
    >
      {label}
    </span>
  );
};

// ============================================================================
// Main Export: Get Columns by Tab Type
// ============================================================================

/**
 * Returns complete column set for a specific write-off tab
 *
 * @param type - Tab type: 'proposed', 'approved', 'processed', 'rejected'
 * @param config - Configuration for actions and display options
 * @returns Column arrays for both NONE and CLIENT groupBy modes
 */
export function getGroupByColumns(
  type: WriteOffTabType,
  onOpenActivity?: (row: WriteOff) => void,
  actionConfig?: ActionColumnConfig & { activityConfig?: ActivityColumnConfig }
): Record<GroupByOption.NONE | GroupByOption.CLIENT, DataTableColumn[]> {
  // Base columns for all tabs
  const baseColumns: DataTableColumn[] = [
    createInvoiceColumn(type === 'proposed'),
    // clientName removed for flat view as requested in plan, but kept for client view?
    // Wait, plan said: INV #, Matter, Category & Type...
    // Screenshot shows: INV #, MATTER, CATEGORY & TYPE...
    // Client is NOT in the main columns in screenshot, but might be under Matter or separate?
    // Screenshot 1 "All Write-Offs" shows: "02130-Internal Frau..." (Matter) and below it "Anh Hoang Alexander & Whi..." (Client Name probably)

    {
      key: 'matterDisplayNumber',
      header: 'Matter',
      align: 'left',
      isResizable: true,
      defaultWidth: 250,
      renderCell: (row: WriteOff) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {row.matterDisplayNumber}
          </span>
          <span className="text-xs text-gray-500 truncate mt-0.5" title={row.clientName}>
            {row.clientName}
          </span>
        </div>
      ),
    },
    createCategoryTypeColumn(),
    // Write-Off Status column - only show for All tab, moved before amounts per screenshot
    ...(type === 'all'
      ? [
          {
            key: 'writeOffStatus',
            header: 'Write-Off Status',
            align: 'center' as const,
            isResizable: true,
            defaultWidth: 140,
            renderCell: (row: WriteOff) => (
              <AllTabStatusBadge status={row.writeOffStatus as WriteOffStatus} />
            ),
          } as DataTableColumn,
        ]
      : []),
    {
      key: 'total',
      header: 'INV Amount',
      align: 'left',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (row: WriteOff) => (
        <div className="flex flex-col min-w-[6rem]">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Invoice Amount</span>
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {numberUtil.formatCurrency(row.total ?? 0)}
          </span>

          <div className="flex gap-2 text-xs mt-1 justify-between">
            <span className="text-gray-600 dark:text-gray-400">Due</span>
            <span className="text-green-600 font-medium">
              {numberUtil.formatCurrency(row.outstandingBalance ?? 0)}
            </span>
          </div>

          <div className="flex gap-2 text-xs justify-between">
            <span className="text-gray-600 dark:text-gray-400">Paid</span>
            <span className="text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(row.paid ?? 0)}
            </span>
          </div>

          <div className="flex gap-2 text-xs justify-between">
            <span className="text-gray-600 dark:text-gray-400">Credits</span>
            <span className="text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(row.creditsIssued ?? 0)}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'writeOffAmount',
      header: 'Write-off Amount',
      align: 'left',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (row: WriteOff) => (
        <div className="flex flex-col min-w-[6rem]">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">
            Write-Off Amount
          </span>
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {numberUtil.formatCurrency(row.writeOff?.amount ?? row?.amount ?? 0)}
          </span>

          {[
            WriteOffType.CLIENT_REFUSED,
            WriteOffType.CUTS_APPLIED,
            WriteOffType.AUTO_PROPOSED,
          ].includes(row.writeOff?.type as WriteOffType) && (
            <>
              <div className="flex gap-2 text-xs mt-1 justify-between">
                <span className="text-gray-600 dark:text-gray-400">Fees</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {numberUtil.formatCurrency(row.writeOff?.feesCut ?? 0)}
                </span>
              </div>

              <div className="flex gap-2 text-xs justify-between">
                <span className="text-gray-600 dark:text-gray-400">Expenses</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {numberUtil.formatCurrency(row.writeOff?.expensesCut ?? 0)}
                </span>
              </div>
            </>
          )}
        </div>
      ),
    },
    createTextColumn({
      key: 'comment',
      header: 'Comment',
      width: 150,
      isSortable: false,
      isResizable: true,
      showTooltip: true,
    }), // Add Comment Column
    // appealStatusColumn, // Removed from screenshot match
    // paymentSummaryColumn, // Removed from screenshot match - likely inside drawer now or simplified
    {
      key: 'finalAppeal',
      header: 'Final Appeal',
      align: 'center',
      isResizable: false,
      defaultWidth: 50,
      renderCell: (row: WriteOff) => <Checkbox checked={row.writeOff?.finalAppeal ?? false} />,
    },
    createInvoiceActionColumn(onOpenActivity),
    ...(actionConfig ? [createActionColumn(actionConfig)] : []),
  ];

  return {
    [GroupByOption.NONE]: baseColumns,
    [GroupByOption.CLIENT]: groupByClient(),
  };
}

export function getWriteOffColumns(config: WriteOffColumnsConfig): DataTableColumn<WriteOffItem>[] {
  return [
    typeColumn,
    categoryColumn,
    {
      key: 'writeOffAmount',
      header: 'Write-off Amount',
      align: 'left',
      isSortable: true,
      isResizable: true,
      defaultWidth: 150,
      renderCell: (row: WriteOffItem) => (
        <div className="flex flex-col min-w-[6rem]">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">
            Write-Off Amount
          </span>
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {numberUtil.formatCurrency(row.amount ?? 0)}
          </span>

          <div className="flex gap-2 text-xs mt-1 justify-between">
            <span className="text-gray-600 dark:text-gray-400">Fees</span>
            <span className="text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(row.feesCut ?? 0)}
            </span>
          </div>

          <div className="flex gap-2 text-xs justify-between">
            <span className="text-gray-600 dark:text-gray-400">Expenses</span>
            <span className="text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(row.expensesCut ?? 0)}
            </span>
          </div>
        </div>
      ),
    },
    createTextColumn({
      key: 'comment',
      header: 'Comment',
      width: 150,
      isSortable: false,
      isResizable: true,
      showTooltip: false,
      getValue: (row: WriteOff) => row.comment,
    }),
    createActionColumn(config),
  ];
}
